import type { QA } from '../components/Faq'

export interface CarrierFaqInput {
  name: string
  scac: string
  coverage: string
  source: string
}

// Source-of-schedule sentence: MUST stay byte-identical to sourcingLine() in prerender.carriers.mjs
// and CarrierPage.tsx so the visible answer, the hydrated app, and the FAQPage JSON-LD all match.
function sourcingLine(c: CarrierFaqInput): string {
  if (c.source === 'connector')
    return `${c.name} sailings are pulled directly from ${c.name}'s own e-service, then normalised into the same schedule shape as every other line.`
  if (c.source === 'official_api')
    return `${c.name} sailings come from ${c.name}'s published schedule API, normalised into the same schedule shape as every other line.`
  return `${c.name} forward sailings are sourced from ${c.name}'s published schedule feed and normalised into the same schedule shape as every other line.`
}

// Per-carrier Q&A templated from the carrier's own fields. Kept byte-identical to carrierFaq() in
// prerender.carriers.mjs (a build-time check in prerender.mjs asserts the two agree). Methodology
// only. Never a fabricated reliability figure.
export function carrierFaq(c: CarrierFaqInput): QA[] {
  return [
    {
      q: `How reliable is ${c.name} on my lane?`,
      a: `${c.name}'s on-time reliability varies by lane, so SchedulesMCP scores it per route from actual versus scheduled arrivals rather than quoting one figure. Enter your origin and destination in the Compare tool to see ${c.name}'s observed on-time percentage and transit time on that exact lane; lanes with too few tracked arrivals are marked early-data instead of guessed.`,
    },
    {
      q: `How do I compare ${c.name} against other carriers?`,
      a: `Open the Compare tool, enter an origin and destination, and ${c.name} is ranked side by side with every other carrier on that lane by observed on-time reliability, transit time, next departure and sailing frequency, so you can see before you book whether ${c.name} or a rival is the better call.`,
    },
    {
      q: `Where do ${c.name} sailing schedules come from?`,
      a: `${sourcingLine(c)}`,
    },
    {
      q: `What trades does ${c.name} cover?`,
      a: `${c.name} (${c.scac}) covers ${c.coverage}. SchedulesMCP normalises those forward sailings into the same shape as every other line so they rank directly against each other.`,
    },
  ]
}
